import { Button, Checkbox, DatePicker, Form, Input, Select, Space } from "antd";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import DateIcon from "../../../assets/icons/DateIcon";
import SelectDropdownLgIcon from "../../../assets/icons/SelectDropdownLgIcon";
import { dateFormat, dbDateFormat } from "../../../constants/constants";
import { useGetEmployeesQuery } from "../../../features/employee/employeeApi";
import {
  useGetLeaveApplicationDetailsQuery,
  useUpdateLeaveApplicationMutation,
} from "../../../features/leaveApplication/leaveApplicationApi";
import { useGetLeaveTypeListQuery } from "../../../features/leaveType/leaveTypeApi";
import useError from "../../../hooks/useError";
import { toast } from "../../../lib/toast";
import { ModalComponentProps } from "../../../types";
import { selectArrayOption, selectSearchOption } from "../../../utils/utils";
import SelectImgNameTitle from "../../common/SelectImgNameTitle";
import CustomModal from "../../ui/CustomModal";
import Loader from "../../ui/Loader";

export default function EditLeaveModal({
  handleClose,
  ...rest
}: ModalComponentProps) {
  const [form] = Form.useForm();
  const { viewId } = useParams();
  const [isHalfDay, setIsHalfDay] = useState(false);
  const {
    data: leaveTypeListData,
    isError: isLeaveTypeError,
    error: leaveTypeError,
  } = useGetLeaveTypeListQuery({
    params: {
      is_published: 1,
    },
  });
  const {
    isError: isEmployeeError,
    error: employeeError,
    data: employeeData,
  } = useGetEmployeesQuery({
    params: {
      limit: 0,
      skip: 0,
      status: "active",
    },
  });
  const {
    isLoading: isDetailsLoading,
    isError: isDetailsError,
    error: detailsError,
    data: detailsData,
  } = useGetLeaveApplicationDetailsQuery(Number(viewId), {
    skip: !viewId,
  });

  const [updateLeaveApplication, { isSuccess, isLoading, isError, error }] =
    useUpdateLeaveApplicationMutation();

  // useError(isLeaveTypeError, leaveTypeError, "Get leave type data fail");

  useError(isEmployeeError, employeeError, "Get employee data fail");
  useError(
    isDetailsError,
    detailsError,
    "Failed fetching leave application details"
  );
  useError(isError, error, "Leave application update fail");
  useError(isLeaveTypeError, leaveTypeError, "Get leave list fail");

  const onFinish = (values: any) => {
    const { date, ...rest } = values;

    if (viewId)
      updateLeaveApplication({
        id: Number(viewId),
        body: {
          ...rest,
          start_date: date?.[0]?.format(dbDateFormat),
          end_date: date?.[1]?.format(dbDateFormat),
          is_half_day: isHalfDay ? 1 : 0,
        },
      });
  };

  useEffect(() => {
    if (Object.keys(detailsData?.data?.leave_application || {})) {
      const details = detailsData?.data?.leave_application;

      form.setFieldsValue({
        leave_type_id: details?.leave_type?.id,
        date: [dayjs(details?.start_date), dayjs(details?.end_date)],
        half_day_configuration: details?.half_day_configuration,
        substitute_id: details?.substitute?.id,
        note: details?.note,
      });

      setIsHalfDay(details?.is_half_day ? true : false);
    }
  }, [detailsData?.data?.leave_application, form]);

  // success toast message
  useEffect(() => {
    if (isSuccess) {
      toast("success", "Leave application update successfully");
      return handleClose();
    }
  }, [handleClose, isSuccess]);

  return (
    <CustomModal
      title="Update Leave"
      {...rest}
      handleClose={handleClose}
      width={460}
      footer={null}
    >
      <Form
        className="custom-form"
        layout="vertical"
        requiredMark="optional"
        size="large"
        onFinish={onFinish}
        form={form}
      >
        {isDetailsLoading ? (
          <Loader />
        ) : (
          <>
            <div className="mb-2.5">
              <Form.Item
                name="leave_type_id"
                label="Leave Type"
                className="mb-1.5"
                rules={[
                  {
                    required: true,
                    message: "Please select leave type",
                  },
                ]}
              >
                {/* <Input size="large" placeholder="Select" /> */}
                <Select
                  placeholder="Select"
                  size="large"
                  allowClear
                  showSearch
                  filterOption={selectSearchOption}
                  options={selectArrayOption(
                    leaveTypeListData?.data.leave_types,
                    "title",
                    "id"
                  )}
                  suffixIcon={<SelectDropdownLgIcon />}
                />
              </Form.Item>
              {/* <small className="block text-highlight text-sm">
                5.00 Annual Leave left
              </small> */}
            </div>

            <Form.Item
              name="date"
              label="Leave Period"
              className="mb-4"
              rules={[
                {
                  required: true,
                  message: "Please select date",
                },
              ]}
            >
              <DatePicker.RangePicker
                format={dateFormat}
                suffixIcon={<DateIcon width={18} height={20} />}
                style={{ width: "100%" }}
                size="large"
              />
            </Form.Item>

            <Checkbox
              className="mb-4"
              checked={isHalfDay}
              onChange={(e) => setIsHalfDay(e.target.checked)}
            >
              Is Half Day{" "}
            </Checkbox>

            {isHalfDay ? (
              <Form.Item
                name="half_day_configuration"
                label="Half Day Type"
                className="mb-4"
                rules={[
                  {
                    required: true,
                    message: "Please select half day type",
                  },
                ]}
              >
                <Select
                  placeholder="Select"
                  size="large"
                  allowClear
                  showSearch
                  filterOption={selectSearchOption}
                  options={halfDayList}
                  suffixIcon={<SelectDropdownLgIcon />}
                />
              </Form.Item>
            ) : null}

            <Form.Item
              name="substitute_id"
              label="Substitute"
              className="mb-4 substitute_id"
              // rules={[
              //   {
              //     required: true,
              //     message: "Please enter substitute",
              //   },
              // ]}
            >
              <SelectImgNameTitle
                placeholder="Select"
                data={employeeData?.data?.employees}
              />
            </Form.Item>

            <Form.Item
              name="note"
              label="Remarks"
              className="mb-4"
              rules={[{ required: true, message: "Please enter remarks" }]}
            >
              <Input size="large" placeholder="Remarks" />
            </Form.Item>

            <Space className="w-full mt-2" size={12}>
              <Button
                className="normal-dark-button modal-button"
                onClick={handleClose}
                size="middle"
              >
                Cancel
              </Button>
              <Button
                className="modal-button"
                type="primary"
                htmlType="submit"
                size="middle"
                loading={isLoading}
              >
                Update
              </Button>
            </Space>
          </>
        )}
      </Form>
    </CustomModal>
  );
}

const halfDayList = [
  {
    label: "First Half",
    value: "first_half",
  },
  {
    label: "Second Half",
    value: "second_half",
  },
];
